import { useEffect, useRef, useState } from "react";
import ItemsYmap, { MapItem } from "../../../../ui/map/itemYmap";
import FacilityItemList from "./components/FacilityItemList";
import useReitListByBuilding from "../../../../../api/hooks/hooks-reit-facility/use-map-data-by-building-id-reit-list";
import Loader from "../../../../ui/loader";
import styles from "./styles.module.scss";
import useMapList from "../../../../../api/hooks/hooks-reit-facility/use-map-data-reit";
import useOpenModal from "../../../../ui/modal/hooks/useOpenModal";
import FacilityCardModal from "../modals/facilityCardModal";

const CLOSE_ICON = "../icons/icon-close.svg";

export interface IFacilityReitItem {
  address: string;
  id: number;
  title: string;
  img: { url?: string; name?: string };
  price: number;
  pricePerSqM?: number;
}

interface IReitFacilityMap {
  facilityTypeIds: number[];
  isLoadingType: boolean;
  heightMap: number;
  isResetSelectedMapItem: boolean;
  onResetSelectedMapItem: () => void;
}

const ReitFacilityMap = ({
  facilityTypeIds,
  isLoadingType,
  heightMap,
  isResetSelectedMapItem,
  onResetSelectedMapItem,
}: IReitFacilityMap) => {
  const [selectedMapItem, setSelectedMapItem] = useState<MapItem>();
  const [currentFacilityId, setCurrentFacilityId] = useState<number>();

  const currentFacilityIdStable = useRef<number>(undefined);
  currentFacilityIdStable.current = currentFacilityId;

  const handleClickFacility = (id: number) => {
    setCurrentFacilityId(id);
    handleChangeModal(true);
  };

  const {
    data,
    error: errorMapList,
    isMutating: isMutatingMapList,
    trigger: triggerMapList,
  } = useMapList(true);

  useEffect(() => {
    triggerMapList({ typeIds: facilityTypeIds });
  }, [facilityTypeIds]);

  const {
    reitList,
    error: errorListByBuilding,
    isMutating,
    trigger,
  } = useReitListByBuilding(selectedMapItem?.id ?? null, true);

  const { offsetY, open, handleChangeModal } = useOpenModal();

  const handleClickBuilding = (item: MapItem) => {
    setSelectedMapItem((prev) => {
      if (prev?.id === item.id && currentFacilityIdStable.current) {
        handleClickFacility(currentFacilityIdStable.current);
      } else {
        return item;
      }
    });
  };

  useEffect(() => {
    if (selectedMapItem?.id && !isResetSelectedMapItem) {
      trigger({ typeIds: facilityTypeIds });
    }
  }, [selectedMapItem?.id, facilityTypeIds]);

  useEffect(() => {
    if (isResetSelectedMapItem) {
      setSelectedMapItem(undefined);
      onResetSelectedMapItem();
    }
  }, [isResetSelectedMapItem]);

  useEffect(() => {
    if (reitList.length === 1) {
      handleClickFacility(reitList[0].id);
    }
  }, [JSON.stringify(reitList)]);

  if (isMutatingMapList || isLoadingType) {
    return (
      <div className={styles.facilityMap__loader}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.facilityMap} style={{ height: heightMap }}>
      <div className={styles.facilityMap__container}>
        <div className={styles.facilityMap__map}>
          <ItemsYmap
            items={data}
            onItemClick={handleClickBuilding}
            isDisableZoomControl={window.innerWidth < 800}
            ownBehaviors={
              window.innerWidth >= 800
                ? ["disable('scrollZoom')", "drag", "dblClickZoom"]
                : []
            }
          />
        </div>

        {selectedMapItem && reitList.length > 1 && (
          <div className={styles.facilityMap__modal}>
            <div className={styles.facilityMap__modalContainer}>
              {isMutating ? (
                <div className={styles.facilityMap__facilityLoader}>
                  <Loader />
                </div>
              ) : (
                <>
                  <FacilityItemList
                    list={reitList}
                    isLoading={isMutating}
                    onClick={handleClickFacility}
                  />
                  <div
                    className={styles.facilityMap__modalClose}
                    onClick={() => setSelectedMapItem(undefined)}
                  >
                    <img src={CLOSE_ICON} alt="close" />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {currentFacilityId && (
          <FacilityCardModal
            onClose={(value) => handleChangeModal(value)}
            opened={open}
            id={currentFacilityId}
            offsetY={offsetY}
          />
        )}
      </div>
    </div>
  );
};

export default ReitFacilityMap;
